import { useMemo } from "react";
import { useQuery } from "@apollo/client/react";
import {
  connectionRows,
  GetTeamMembersDocument,
  type TeamMemberRow,
  type TeamMembershipRole,
} from "#app/lib/data";
import { buildOrderBy, combineWhereConditions } from "#app/lib/query-utils";
import type { SortingState } from "@salestrak/ui";

/**
 * A team member row plus the one thing the server does not say: whether this
 * person manages the team (membership role "lead" on the wire, "Manager" in
 * UI copy). The role is a property of the TEAM's `leads` list, not of the
 * user, so it is joined here rather than selected.
 */
export type TeamMember = TeamMemberRow & { role: TeamMembershipRole };

export function useTeamMembers(
  teamId: string,
  sorting: SortingState,
  options?: {
    skip?: boolean;
    /** The roster search's predicate (from `buildSearchWhere`), AND-folded
     *  onto the team-membership base condition the hook owns. */
    searchWhere?: Record<string, unknown> | undefined;
    /** The roster's quick-filter chips' predicate, folded the same way. */
    filterWhere?: Record<string, unknown> | undefined;
  },
) {
  const orderBy = useMemo(() => buildOrderBy(sorting, "name"), [sorting]);
  const searchWhere = options?.searchWhere;
  const filterWhere = options?.filterWhere;

  const queryVariables = useMemo(() => {
    const base = { teamIds: { hasSome: [teamId] } };
    const where =
      combineWhereConditions(
        [base, searchWhere, filterWhere].filter(Boolean) as Array<Record<string, unknown>>,
      ) ?? base;
    return { id: teamId, where, orderBy };
  }, [teamId, searchWhere, filterWhere, orderBy]);

  const { data, loading, error, refetch } = useQuery(GetTeamMembersDocument, {
    variables: queryVariables,
    skip: options?.skip ?? false,
    fetchPolicy: "cache-and-network",
  });

  const teamData = data?.team ?? null;

  // Derived from `leads`, which the detail fragment already selects in full.
  // Selecting `leadIds` alongside it would be the same data twice, and one
  // more unbatched query.
  const leadIdSet = useMemo(
    () => new Set((teamData?.leads ?? []).map((u) => u.userId)),
    [teamData?.leads],
  );

  const teamMembers = useMemo<TeamMember[]>(
    () =>
      connectionRows(data?.teamMembers).map((u) => ({
        ...u,
        role: leadIdSet.has(u.userId) ? ("lead" as const) : ("member" as const),
      })),
    [data?.teamMembers, leadIdSet],
  );

  const teamName = teamData?.teamName ?? "Team";
  const leadCount = teamData?.leads?.length ?? 0;

  // From the team detail's own rosters, NOT the (possibly search-filtered)
  // teamMembers connection — the add-members picker must exclude every
  // current member regardless of what the search shows.
  const existingUserIds = useMemo(
    () =>
      Array.from(
        new Set(
          [...(teamData?.leads ?? []), ...(teamData?.members ?? [])].map(
            (u) => u.userId,
          ),
        ),
      ),
    [teamData?.leads, teamData?.members],
  );

  return {
    teamName,
    teamMembers,
    existingUserIds,
    leadCount,
    loading,
    error,
    refetch,
  };
}
